import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSpinner,
  faEdit,
  faTrash,
  faRocket,
  faTimes,
  faHistory,
  faChevronDown,
  faChevronUp,
  faComment,
  faFileAlt,
  faBullseye,
  faStop
} from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { useAccount } from '../contexts/AccountContext';
import { getUserData } from '../utils/userUtils';
import StatusBadge from './StatusBadge';
import MediaPlanDisplay from './MediaPlanDisplay';
import './MediaPlanDetails.css';

/**
 * MediaPlanDetails component for viewing media plan details
 * Shows full revision history with all feedback and responses
 */
const MediaPlanDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAccountIdToUse } = useAccount();
  const [user] = useState(() => getUserData());
  const accountId = getAccountIdToUse();

  const [mediaPlan, setMediaPlan] = useState(null);
  const [revisionHistory, setRevisionHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSubmittingRevision, setIsSubmittingRevision] = useState(false); // Track API call in progress
  const [expandedVersions, setExpandedVersions] = useState({});
  const [pendingRevision, setPendingRevision] = useState(null); // Track in-progress revision

  // Use ref to track polling state across re-renders
  const pollingRef = useRef(false);
  const pollTimeoutRef = useRef(null);
  const pollingStartTimeRef = useRef(null);
  const POLLING_TIMEOUT = 20 * 60 * 1000; // 20 minutes max polling time

  // Channel/Platform display name - normalize for display
  const getChannelName = (channel) => {
    const names = {
      'dv360': 'DV360',
      'ttd': 'The Trade Desk',
      'facebook': 'Facebook',
      'meta': 'Meta',
      'google_ads': 'Google Ads Search',
      'google-ads-search': 'Google Ads Search',
      'google_ads_search': 'Google Ads Search',
      'tiktok': 'TikTok',
      'reddit': 'Reddit',
      'linkedin': 'LinkedIn',
      'madhive': 'MadHive'
    };
    return names[channel?.toLowerCase()] || channel || 'N/A';
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      pollingRef.current = false;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, []);

  // Poll for generation result - handles both initial generation and revisions
  const pollForResult = useCallback(async (messageId, planId, isRevision = false) => {
    if (!pollingRef.current) return; // Stop if component unmounted or polling cancelled

    // Initialize polling start time if not set
    if (!pollingStartTimeRef.current) {
      pollingStartTimeRef.current = Date.now();
    }

    // Check for timeout
    if ((Date.now() - pollingStartTimeRef.current) > POLLING_TIMEOUT) {
      pollingRef.current = false;
      pollingStartTimeRef.current = null;

      // Update the plan status to 'failed' in database
      try {
        await api.patch(getApiUrl(`/api/v2/media-plans/${planId}`), {
          accountId,
          userId: user?.userId || user?.id,
          status: 'failed',
          error: 'Processing timed out after 20 minutes'
        });
        // Refresh data to show updated status
        await fetchFullHistory();
      } catch (updateErr) {
        console.error('Failed to update plan status:', updateErr);
      }

      setError('Processing timed out after 20 minutes. Status has been updated to Failed.');
      if (isRevision) {
        setIsRegenerating(false);
        setPendingRevision(null);
      } else {
        setIsGenerating(false);
      }
      console.log('Polling timed out after 20 minutes - status updated to failed');
      return;
    }

    try {
      const response = await api.get(getApiUrl(`/api/v2/media-planner/status/${messageId}`), {
        params: { accountId, userId: user?.userId || user?.id }
      });

      if (response.data.status === 'completed' && response.data.mediaPlan) {
        pollingRef.current = false;
        pollingStartTimeRef.current = null;

        // Update the plan in database
        await api.patch(getApiUrl(`/api/v2/media-plans/${planId}`), {
          accountId,
          userId: user?.userId || user?.id,
          planData: response.data.mediaPlan,
          status: 'completed'
        });

        // Refresh all data
        await fetchFullHistory();

        if (isRevision) {
          setIsRegenerating(false);
          setPendingRevision(null);
        } else {
          setIsGenerating(false);
        }
      } else if (response.data.status === 'failed' || response.data.status === 'timeout') {
        pollingRef.current = false;
        pollingStartTimeRef.current = null;
        const errorMessage = response.data.errors?.length > 0
          ? response.data.errors.join('. ')
          : (response.data.error || 'Failed to generate media plan');
        setError(errorMessage);

        await api.patch(getApiUrl(`/api/v2/media-plans/${planId}`), {
          accountId,
          userId: user?.userId || user?.id,
          status: 'failed'
        });

        if (isRevision) {
          setIsRegenerating(false);
          setPendingRevision(null);
        } else {
          setIsGenerating(false);
        }
      } else if (pollingRef.current) {
        // Continue polling
        pollTimeoutRef.current = setTimeout(() => pollForResult(messageId, planId, isRevision), 5000);
      }
    } catch (err) {
      console.error('Error polling for result:', err);
      if (pollingRef.current) {
        pollingRef.current = false;
        pollingStartTimeRef.current = null;
        setError('Failed to check generation status');
        if (isRevision) {
          setIsRegenerating(false);
          setPendingRevision(null);
        } else {
          setIsGenerating(false);
        }
      }
    }
  // Note: fetchFullHistory is intentionally not in deps to avoid circular dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, user, POLLING_TIMEOUT]);

  // Fetch the root plan (first version)
  const findRootPlanId = useCallback(async (planId) => {
    try {
      const response = await api.get(getApiUrl(`/api/v2/media-plans/${planId}`), {
        params: { accountId, userId: user?.userId || user?.id }
      });

      if (response.data.parent_plan_id) {
        // Recursively find root
        return findRootPlanId(response.data.parent_plan_id);
      }
      return planId;
    } catch {
      return planId;
    }
  }, [accountId, user]);

  // Fetch full revision history starting from root plan
  const fetchFullHistory = useCallback(async () => {
    if (!accountId || !id) return;

    try {
      // First, find the root plan
      const rootId = await findRootPlanId(id);

      // Fetch root plan
      const rootResponse = await api.get(getApiUrl(`/api/v2/media-plans/${rootId}`), {
        params: { accountId, userId: user?.userId || user?.id }
      });

      if (rootResponse.status !== 200) {
        throw new Error('Failed to fetch root plan');
      }

      const rootPlan = rootResponse.data;

      // Build full history array: root plan + all iterations
      const history = [rootPlan];

      // Use iterations from root plan response if available
      if (rootPlan.iterations && rootPlan.iterations.length > 0) {
        // Fetch full details for each iteration
        for (const iter of rootPlan.iterations) {
          const iterDetail = await api.get(getApiUrl(`/api/v2/media-plans/${iter.id}`), {
            params: { accountId, userId: user?.userId || user?.id }
          });
          if (iterDetail.status === 200) {
            history.push(iterDetail.data);
          }
        }
      }

      // Sort by iteration_number descending (latest first)
      history.sort((a, b) => (b.iteration_number || 0) - (a.iteration_number || 0));

      setRevisionHistory(history);

      // Set the current plan (the one matching the URL id, or the latest)
      const currentPlan = history.find(p => p.id === id) || history[0];
      setMediaPlan(currentPlan);

      // Expand only the latest version by default (first in sorted array)
      const latestVersion = history[0];
      setExpandedVersions({ [latestVersion.id]: true });

      // Check if any plan is processing and start polling
      const processingPlan = history.find(p => p.status === 'processing');
      if (processingPlan && !pollingRef.current) {
        const pollId = processingPlan.message_id || processingPlan.processing_id;
        if (pollId) {
          pollingRef.current = true;
          const isRevision = processingPlan.iteration_number > 0;
          if (isRevision) {
            setIsRegenerating(true);
            setPendingRevision(processingPlan);
          } else {
            setIsGenerating(true);
          }
          pollForResult(pollId, processingPlan.id, isRevision);
        }
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching media plan history:', err);
      setError(err.response?.data?.error || 'Failed to load media plan details');
    } finally {
      setIsLoading(false);
    }
  }, [accountId, id, user, findRootPlanId, pollForResult]);

  useEffect(() => {
    fetchFullHistory();
  }, [fetchFullHistory]);

  // Toggle version expansion
  const toggleVersionExpand = (versionId) => {
    setExpandedVersions(prev => ({
      ...prev,
      [versionId]: !prev[versionId]
    }));
  };

  // Handle delete
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this media plan?')) return;

    try {
      await api.delete(getApiUrl(`/api/v2/media-plans/${id}`), {
        params: {
          accountId,
          userId: user?.userId || user?.id
        }
      });
      navigate('/media-plans');
    } catch (err) {
      console.error('Error deleting media plan:', err);
      setError(err.response?.data?.error || 'Failed to delete media plan');
    }
  };

  // Handle regeneration with feedback - saves new version immediately, polls on same page
  const handleRegenerate = async () => {
    if (!feedback.trim()) return;

    setIsSubmittingRevision(true);
    setError(null);

    try {
      // Get root plan for consistent naming (last in array since sorted descending)
      const rootPlan = revisionHistory[revisionHistory.length - 1] || mediaPlan;
      const rootName = rootPlan.name.replace(/ \(v\d+\)$/, ''); // Remove version suffix if present
      const newIterationNumber = revisionHistory.length;

      const formData = new FormData();
      formData.append('accountId', accountId);
      formData.append('userId', user?.userId || user?.id);
      formData.append('campaignBrief', mediaPlan.campaign_brief);
      formData.append('budget', mediaPlan.total_budget);
      formData.append('startDate', mediaPlan.start_date);
      formData.append('endDate', mediaPlan.end_date);
      formData.append('channels', JSON.stringify(mediaPlan.channels));
      formData.append('feedback', feedback);

      const response = await api.post(getApiUrl('/api/v2/media-planner/generate'), formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data?.messageId) {
        // Save revision immediately with 'processing' status
        const saveResponse = await api.post(getApiUrl('/api/v2/media-plans'), {
          accountId,
          userId: user?.userId || user?.id,
          name: `${rootName} (v${newIterationNumber + 1})`,
          chatId: response.data.chatId,
          messageId: response.data.messageId,
          campaignBrief: mediaPlan.campaign_brief,
          totalBudget: mediaPlan.total_budget,
          startDate: mediaPlan.start_date,
          endDate: mediaPlan.end_date,
          channels: mediaPlan.channels,
          status: 'processing',
          parentPlanId: rootPlan.id, // Always link to root plan
          iterationNumber: newIterationNumber,
          feedback: feedback.trim()
        });

        if (saveResponse.data?.id) {
          // Close dialog and start polling
          setShowFeedbackDialog(false);
          setIsSubmittingRevision(false);
          setIsRegenerating(true);
          setPendingRevision({
            ...saveResponse.data,
            feedback: feedback.trim()
          });
          pollingRef.current = true;
          pollForResult(response.data.messageId, saveResponse.data.id, true);

          // Add to history immediately (at the beginning since sorted descending)
          setRevisionHistory(prev => [{
            ...saveResponse.data,
            feedback: feedback.trim(),
            status: 'processing'
          }, ...prev]);

          // Expand the new version
          setExpandedVersions({ [saveResponse.data.id]: true });

          setFeedback('');
        }
      } else {
        setError('Failed to regenerate media plan: No message ID returned');
        setIsSubmittingRevision(false);
      }
    } catch (err) {
      console.error('Error regenerating media plan:', err);
      setError(err.response?.data?.error || 'Failed to regenerate media plan');
      setIsSubmittingRevision(false);
    }
  };

  // Format helpers
  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  // Loading state
  if (isLoading && !mediaPlan) {
    return (
      <div className="media-plan-details-loading">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <p>Loading media plan details...</p>
      </div>
    );
  }

  // Error state
  if (error && !mediaPlan) {
    return (
      <div className="media-plan-details-error">
        <p>{error}</p>
        <button onClick={() => navigate('/media-plans')}>Back to Media Plans</button>
      </div>
    );
  }

  if (!mediaPlan) return null;

  return (
    <div className="media-plan-details-container">
      {/* Header - shows root plan info */}
      <div className="media-plan-detail-header">
        <button
          onClick={() => navigate('/media-plans')}
          className="back-button"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Media Plans
        </button>

        <div className="media-plan-info">
          <h1>{(revisionHistory[0] || mediaPlan).name.replace(/ \(v\d+\)$/, '')}</h1>
          <div className="media-plan-meta">
            <span className="budget">{formatCurrency(mediaPlan.total_budget)}</span>
            <span className="dates">{formatDate(mediaPlan.start_date)} - {formatDate(mediaPlan.end_date)}</span>
            {revisionHistory.length > 1 && (
              <span className="version-count">{revisionHistory.length} versions</span>
            )}
          </div>
        </div>

        <div className="header-actions">
          <button
            className="btn btn-secondary"
            onClick={() => setShowFeedbackDialog(true)}
            disabled={isRegenerating || isGenerating}
          >
            <FontAwesomeIcon icon={faEdit} />
            Revise Plan
          </button>
          <button
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={isRegenerating || isGenerating}
          >
            <FontAwesomeIcon icon={faTrash} />
            Delete
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="media-plan-error-banner">
          <p>{error}</p>
          <button onClick={() => setError(null)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* Generating state - shown as banner when initial plan is being generated */}
      {(isGenerating || (revisionHistory.length === 1 && revisionHistory[0]?.status === 'processing')) && (
        <div className="generating-banner">
          <FontAwesomeIcon icon={faSpinner} spin />
          <span>Generating your media plan... This may take a minute or two.</span>
          <button
            className="stop-polling-btn"
            onClick={async () => {
              // Stop polling first
              pollingRef.current = false;
              if (pollTimeoutRef.current) {
                clearTimeout(pollTimeoutRef.current);
                pollTimeoutRef.current = null;
              }
              pollingStartTimeRef.current = null;
              setIsGenerating(false);
              // Update status to 'stopped' in database
              const planToUpdate = revisionHistory.find(p => p.status === 'processing') || revisionHistory[0];
              if (planToUpdate?.id) {
                try {
                  await api.patch(getApiUrl(`/api/v2/media-plans/${planToUpdate.id}`), {
                    accountId,
                    userId: user?.userId || user?.id,
                    status: 'stopped'
                  });
                  await fetchFullHistory(); // Refresh to show updated status
                } catch (err) {
                  console.error('Failed to update plan status:', err);
                }
              }
            }}
            title="Stop polling"
          >
            <FontAwesomeIcon icon={faStop} />
            Stop Polling
          </button>
        </div>
      )}

      {/* Regenerating state - shown as banner */}
      {isRegenerating && (
        <div className="regenerating-banner">
          <FontAwesomeIcon icon={faSpinner} spin />
          <span>Generating new version based on your feedback...</span>
          <button
            className="stop-polling-btn"
            onClick={async () => {
              // Stop polling first
              pollingRef.current = false;
              if (pollTimeoutRef.current) {
                clearTimeout(pollTimeoutRef.current);
                pollTimeoutRef.current = null;
              }
              pollingStartTimeRef.current = null;
              setIsRegenerating(false);
              setPendingRevision(null);
              // Update status to 'stopped' in database
              const planToUpdate = pendingRevision || revisionHistory.find(p => p.status === 'processing');
              if (planToUpdate?.id) {
                try {
                  await api.patch(getApiUrl(`/api/v2/media-plans/${planToUpdate.id}`), {
                    accountId,
                    userId: user?.userId || user?.id,
                    status: 'stopped'
                  });
                  await fetchFullHistory(); // Refresh to show updated status
                } catch (err) {
                  console.error('Failed to update plan status:', err);
                }
              }
            }}
            title="Stop polling"
          >
            <FontAwesomeIcon icon={faStop} />
            Stop Polling
          </button>
        </div>
      )}

      {/* Campaign Brief & Channels - shown once at top in a combined card */}
      <div className="campaign-overview-card">
        <div className="overview-section brief-section">
          <div className="overview-header">
            <div className="overview-icon brief-icon">
              <FontAwesomeIcon icon={faFileAlt} />
            </div>
            <h3>Campaign Brief</h3>
          </div>
          <div className="overview-content">
            <p className="brief-text">{mediaPlan.campaign_brief || 'No brief provided'}</p>
          </div>
        </div>

        <div className="overview-divider" />

        <div className="overview-section channels-section">
          <div className="overview-header">
            <div className="overview-icon channels-icon">
              <FontAwesomeIcon icon={faBullseye} />
            </div>
            <h3>Target Channels</h3>
            <span className="channel-count">{(mediaPlan.channels || []).length}</span>
          </div>
          <div className="overview-content">
            <div className="channels-grid">
              {(mediaPlan.channels || []).map(channel => (
                <div key={channel} className="channel-card">
                  <span className="channel-name">{getChannelName(channel)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Revision Timeline - shows all versions */}
      <div className="media-plan-section revision-timeline-section">
        <h2>
          <FontAwesomeIcon icon={faHistory} />
          Plan History ({revisionHistory.length} version{revisionHistory.length !== 1 ? 's' : ''})
        </h2>

        <div className="revision-timeline">
          {revisionHistory.map((version, index) => {
            // Since array is sorted descending (latest first), calculate actual version number
            const versionNum = revisionHistory.length - index;
            const isOriginal = version.iteration_number === 0;
            const isLatest = index === 0;

            return (
            <div key={version.id} className={`revision-entry ${expandedVersions[version.id] ? 'expanded' : ''} ${version.status === 'processing' ? 'processing' : ''}`}>
              {/* Version Header */}
              <div
                className="revision-header"
                onClick={() => toggleVersionExpand(version.id)}
              >
                <div className="revision-marker">
                  <span className="version-number">v{versionNum}</span>
                  {index < revisionHistory.length - 1 && <div className="timeline-connector" />}
                </div>

                <div className="revision-summary">
                  <div className="revision-title">
                    <span className="version-label">
                      {isOriginal ? 'Original Plan' : `Revision ${version.iteration_number}`}
                    </span>
                    <StatusBadge status={version.status || 'completed'} />
                    {isLatest && <span className="latest-badge">Latest</span>}
                  </div>
                  <div className="revision-meta">
                    <span>{formatDate(version.created_at)}</span>
                  </div>
                </div>

                <div className="revision-toggle">
                  <FontAwesomeIcon icon={expandedVersions[version.id] ? faChevronUp : faChevronDown} />
                </div>
              </div>

              {/* Feedback that prompted this version */}
              {version.feedback && (
                <div className="revision-feedback">
                  <FontAwesomeIcon icon={faComment} />
                  <div className="feedback-content">
                    <span className="feedback-label">Revision feedback:</span>
                    <p>{version.feedback}</p>
                  </div>
                </div>
              )}

              {/* Expanded Content - Media Plan */}
              {expandedVersions[version.id] && (
                <div className="revision-content">
                  {version.status === 'processing' ? (
                    <div className="version-processing">
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span>{isOriginal ? 'Generating your media plan...' : 'Generating plan based on feedback...'}</span>
                    </div>
                  ) : version.plan_data ? (
                    <MediaPlanDisplay
                      plan={version.plan_data}
                      onEdit={isLatest ? () => setShowFeedbackDialog(true) : null}
                      mediaPlanName={(revisionHistory[0] || mediaPlan)?.name?.replace(/ \(v\d+\)$/, '')}
                    />
                  ) : (
                    <div className="no-plan-data">
                      <p>Plan data not available</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
          })}

          {/* Pending Revision Indicator */}
          {pendingRevision && !revisionHistory.find(v => v.id === pendingRevision.id) && (
            <div className="revision-entry processing">
              <div className="revision-header">
                <div className="revision-marker">
                  <span className="version-number">v{revisionHistory.length + 1}</span>
                </div>
                <div className="revision-summary">
                  <div className="revision-title">
                    <span className="version-label">New Revision</span>
                    <StatusBadge status="processing" />
                  </div>
                </div>
              </div>
              {pendingRevision.feedback && (
                <div className="revision-feedback">
                  <FontAwesomeIcon icon={faComment} />
                  <div className="feedback-content">
                    <span className="feedback-label">Revision feedback:</span>
                    <p>{pendingRevision.feedback}</p>
                  </div>
                </div>
              )}
              <div className="revision-content">
                <div className="version-processing">
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Generating plan based on feedback...</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Feedback Dialog */}
      {showFeedbackDialog && (
        <div className="feedback-dialog-overlay">
          <div className="feedback-dialog">
            <div className="dialog-header">
              <h3>Revise Media Plan</h3>
              <button
                onClick={() => !isSubmittingRevision && setShowFeedbackDialog(false)}
                className="close-btn"
                disabled={isSubmittingRevision}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="dialog-content">
              <p>Provide feedback to improve the media plan. Our AI will generate a new version based on your input.</p>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="e.g., Increase focus on social media channels, reduce budget allocation for display ads..."
                rows={5}
                disabled={isSubmittingRevision}
              />
            </div>
            <div className="dialog-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowFeedbackDialog(false)}
                disabled={isSubmittingRevision}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleRegenerate}
                disabled={!feedback.trim() || isSubmittingRevision}
              >
                {isSubmittingRevision ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Submitting...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faRocket} />
                    Regenerate Plan
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaPlanDetails;

